import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Toaster } from "./components/ui/sonner";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import MapView from "./pages/MapView";
import Matches from "./pages/Matches";
import CreateMatch from "./pages/CreateMatch";
import MatchDetails from "./pages/MatchDetails";
import Leaderboard from "./pages/Leaderboard";
import WalletPage from "./pages/Wallet";
import Profile from "./pages/Profile";
import Teams from "./pages/Teams";
import TeamDetails from "./pages/TeamDetails";
import Tournaments from "./pages/Tournaments";
import TournamentDetails from "./pages/TournamentDetails";
import Leagues from "./pages/Leagues";
import LeagueDetails from "./pages/LeagueDetails";
import FavoritePlayers from "./pages/FavoritePlayers";

// Player Booking Pages
import FieldsList from "./pages/FieldsList";
import BookField from "./pages/BookField";
import MyBookings from "./pages/MyBookings";

// Field Owner Pages
import OwnerDashboard from "./pages/OwnerDashboard";
import OwnerFields from "./pages/OwnerFields";
import OwnerBookings from "./pages/OwnerBookings";
import OwnerEarnings from "./pages/OwnerEarnings";
import OwnerCalendar from "./pages/OwnerCalendar";

// Admin Pages
import AdminDashboard from "./pages/AdminDashboard";
import AdminPlayers from "./pages/AdminPlayers";
import AdminFieldOwners from "./pages/AdminFieldOwners";
import AdminTeams from "./pages/AdminTeams";
import AdminTournaments from "./pages/AdminTournaments";
import AdminLeagues from "./pages/AdminLeagues";
import AdminReports from "./pages/AdminReports";
import AdminResults from "./pages/AdminResults";

// Notifications
import Notifications from "./pages/Notifications";

// Challenges
import Challenges from "./pages/Challenges";

// About
import About from "./pages/About";

// Community
import Community from "./pages/Community";

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-tunis-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin Route Component
const AdminRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-tunis-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Field Owner Route Component
const FieldOwnerRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-tunis-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (user?.role !== 'field_owner') {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Public Route - redirects based on role if already logged in
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-12 h-12 border-4 border-tunis-teal border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated && user) {
    // Redirect based on user role
    if (user.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user.role === 'field_owner') {
      return <Navigate to="/owner" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicRoute><Landing /></PublicRoute>} />
      <Route path="/about" element={<About />} />
      <Route path="/community" element={<Community />} />
      <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/map" element={<ProtectedRoute><MapView /></ProtectedRoute>} />
      <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
      <Route path="/matches/create" element={<ProtectedRoute><CreateMatch /></ProtectedRoute>} />
      <Route path="/matches/:id" element={<ProtectedRoute><MatchDetails /></ProtectedRoute>} />
      <Route path="/leaderboard" element={<ProtectedRoute><Leaderboard /></ProtectedRoute>} />
      <Route path="/wallet" element={<ProtectedRoute><WalletPage /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      
      {/* Player Booking Routes */}
      <Route path="/fields" element={<ProtectedRoute><FieldsList /></ProtectedRoute>} />
      <Route path="/book/:fieldId" element={<ProtectedRoute><BookField /></ProtectedRoute>} />
      <Route path="/bookings" element={<ProtectedRoute><MyBookings /></ProtectedRoute>} />
      
      {/* Teams */}
      <Route path="/teams" element={<ProtectedRoute><Teams /></ProtectedRoute>} />
      <Route path="/teams/:id" element={<ProtectedRoute><TeamDetails /></ProtectedRoute>} />
      
      {/* Favorite Players */}
      <Route path="/favorites" element={<ProtectedRoute><FavoritePlayers /></ProtectedRoute>} />
      
      {/* Tournaments */}
      <Route path="/tournaments" element={<ProtectedRoute><Tournaments /></ProtectedRoute>} />
      <Route path="/tournaments/:id" element={<ProtectedRoute><TournamentDetails /></ProtectedRoute>} />
      
      {/* Leagues */}
      <Route path="/leagues" element={<ProtectedRoute><Leagues /></ProtectedRoute>} />
      <Route path="/leagues/:id" element={<ProtectedRoute><LeagueDetails /></ProtectedRoute>} />
      
      {/* Notifications */}
      <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
      
      {/* Challenges */}
      <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
      
      {/* Field Owner Routes */}
      <Route path="/owner" element={<FieldOwnerRoute><OwnerDashboard /></FieldOwnerRoute>} />
      <Route path="/owner/dashboard" element={<FieldOwnerRoute><OwnerDashboard /></FieldOwnerRoute>} />
      <Route path="/owner/fields" element={<FieldOwnerRoute><OwnerFields /></FieldOwnerRoute>} />
      <Route path="/owner/bookings" element={<FieldOwnerRoute><OwnerBookings /></FieldOwnerRoute>} />
      <Route path="/owner/earnings" element={<FieldOwnerRoute><OwnerEarnings /></FieldOwnerRoute>} />
      <Route path="/owner/calendar" element={<FieldOwnerRoute><OwnerCalendar /></FieldOwnerRoute>} />
      
      {/* Admin Routes */}
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/players" element={<AdminRoute><AdminPlayers /></AdminRoute>} />
      <Route path="/admin/field-owners" element={<AdminRoute><AdminFieldOwners /></AdminRoute>} />
      <Route path="/admin/teams" element={<AdminRoute><AdminTeams /></AdminRoute>} />
      <Route path="/admin/tournaments" element={<AdminRoute><AdminTournaments /></AdminRoute>} />
      <Route path="/admin/leagues" element={<AdminRoute><AdminLeagues /></AdminRoute>} />
      <Route path="/admin/reports" element={<AdminRoute><AdminReports /></AdminRoute>} />
      <Route path="/admin/results" element={<AdminRoute><AdminResults /></AdminRoute>} />
      
      {/* Catch all - redirect to landing */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
